import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import FacebookIcon from '@src/assets/icons/icon-facebook.svg';
import MessengerIcon from '@src/assets/icons/icon-messenger.svg';
import ZaloIcon from '@src/assets/icons/icon-zalo.svg';
import GmailIcon from '@src/assets/icons/icon-gmail.svg';
import { Button } from '@mui/material';
import { getReferralContent } from '@src/services/referral';
import { getMobileOperatingSystem } from '@src/utils/osMobile';
import { MOBILE_OS } from '@src/constants/os';
import {
  FACEBOOK_MESSENGER_INFO,
  GMAIL_INFO,
  REFERRAL_EMAIL_TITLE,
  ZALO_INFO,
} from '@src/constants/referral';
import { FB_APP_ID, REFERRAL_LINK, ZALO_APP_ID } from '@src/configs';
import { getStudioFreePackage } from '@src/services/package';
import { StyledReferralSharing } from './index.style';

const ReferralCodeSharing = ({ referralLink, referralCode }) => {
  const { t, i18n } = useTranslation();
  const referralConfig = useSelector((state) => state.referralConfig);
  const { packages } = useSelector((state) => state.packages);

  const { language } = i18n;
  const freePackage = getStudioFreePackage(packages);
  const { introductoryDailyCredits } = freePackage?.freemiumConfig || {};
  const { charsPerPoint, refereePoints } = referralConfig || {};
  const refereeReward =
    refereePoints * charsPerPoint + introductoryDailyCredits;

  const shareUrl = referralLink || REFERRAL_LINK;

  const osMobile = getMobileOperatingSystem();
  const isMobile = [
    MOBILE_OS.ANDROID,
    MOBILE_OS.IOS,
    MOBILE_OS.WIN_PHONE,
  ].includes(osMobile);

  const emailSubject = encodeURIComponent(REFERRAL_EMAIL_TITLE);
  const referralContent = getReferralContent(
    referralCode,
    referralLink,
    refereeReward.toLocaleString(language),
  );

  const encodedReferralContent = encodeURIComponent(referralContent);

  const shareOnMessengerURL = () => {
    const encodedUrl = encodeURIComponent(shareUrl);

    window.open(
      `${FACEBOOK_MESSENGER_INFO.MESS_LINK}/?text=${encodedReferralContent}%20${encodedUrl}`,
      '_blank',
      'width=600,height=400,scrollbars=yes,resizable=yes',
    );
  };

  // Messenger Share Handler
  const handleShareByMessenger = async () => {
    if (isMobile && navigator.share) {
      try {
        // No encode with mobile web
        await navigator.share({
          text: referralContent,
        });
      } catch (error) {
        shareOnMessengerURL();
      }
    } else {
      shareOnMessengerURL();
    }
  };

  const emailHref = isMobile
    ? `${GMAIL_INFO.MOBILE_LINK}?subject=${emailSubject}&body=${encodedReferralContent}`
    : `${GMAIL_INFO.WEB_LINK}&su=${emailSubject}&body=${encodedReferralContent}`;

  useEffect(() => {
    const existingScript = document.querySelector(
      `script[src="${ZALO_INFO.SCRIPT}"]`,
    );
    if (existingScript) {
      existingScript.remove();
    }

    const container = document.getElementById('zalo-share-wrapper');
    if (container) {
      container.innerHTML = `
        <div
          class="zalo-share-button"
          data-href="${shareUrl}"
          data-oaid="${ZALO_APP_ID}"
          data-layout="${ZALO_INFO.CUSTOM_APP_LAYOUT}"
          data-color="${ZALO_INFO.APP_COLOR}"
          data-customize="true"
        >
          <img src="${ZaloIcon}" alt="Zalo" className="zalo-share-button" />
        </div>
      `;
    }

    const script = document.createElement('script');
    script.src = ZALO_INFO.SCRIPT;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, [shareUrl]);

  return (
    <StyledReferralSharing>
      <div className="section-divider">
        <span>{t('referralSharing')}</span>
      </div>
      <div className="social-share-wrapper">
        <a
          className="share-button"
          href={`${
            FACEBOOK_MESSENGER_INFO.FB_LINK
          }?app_id=${FB_APP_ID}&display=popup&href=${encodeURIComponent(
            shareUrl,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Share via Facebook"
        >
          <img src={FacebookIcon} alt="Facebook" />
        </a>

        <Button
          className="share-button"
          onClick={handleShareByMessenger}
          title="Share via Messenger"
        >
          <img src={MessengerIcon} className="icon-messenger" alt="Messenger" />
        </Button>

        <div key={shareUrl} id="zalo-share-wrapper" />

        <a
          className="share-button"
          href={emailHref}
          target="_blank"
          rel="noopener noreferrer"
          title="Share via Email"
        >
          <img src={GmailIcon} alt="Gmail" />
        </a>
      </div>
    </StyledReferralSharing>
  );
};

export default ReferralCodeSharing;
